import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

/**
 * Compact KPI card — neutral surface, tinted accent icon, muted label, bold value.
 *
 * Color is reserved for *meaning*, not decoration, and lives in the icon chip / border.
 * Pass `color` as a palette key (`primary` | `success` | `warning` | `error` | `info` |
 * `secondary`) or `neutral`. The value figure always uses the `text.ink` token so it stays
 * legible on the tinted surface regardless of the semantic accent.
 */
export default function StatCard({ icon: Icon, label, value, hint, color = 'primary', sx }) {
    const theme = useTheme();
    const accent = color === 'neutral'
        ? theme.palette.text.secondary
        : (theme.palette[color]?.main ?? theme.palette.primary.main);
    // All text uses the ink token (deep teal in light, chalk in dark) for one legible,
    // unified color on the tinted surface; hierarchy comes from size/weight. Semantic
    // color lives only in the icon chip and border, never in the text itself.
    const ink = theme.palette.text.ink;

    return (
        <Card
            variant="outlined"
            sx={{
                height: '100%',
                p: 0,
                bgcolor: alpha(accent, 0.04),
                borderColor: alpha(accent, 0.3),
                '& .MuiTypography-root': { m: 0, textAlign: 'start' },
                ...sx,
            }}
        >
            <CardContent
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    p: 2,
                    '&:last-child': { pb: 2 },
                }}
            >
                {Icon && (
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(accent, 0.12),
                            color: accent,
                        }}
                    >
                        <Icon fontSize="small" />
                    </Box>
                )}
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ color: ink, mb: 0.5 }}>
                        {label}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: ink, lineHeight: 1.2 }}>
                        {value}
                    </Typography>
                    {hint && (
                        <Typography variant="caption" sx={{ color: ink, opacity: 0.85, display: 'block', mt: 0.5 }}>
                            {hint}
                        </Typography>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
